import { StringToUInt8 } from "@pdfking/tfunc"

import type { NewDocument } from "../types/NewDocument"
import type { PDFDocument } from "../types/PDFDocument"
import { GetLastError } from "./GetLastError"

/**
 * Copies pages from one PDF document to another at a specified position
 *
 * @param from_doc Source PDF document (document to copy FROM)
 * @param to_doc Destination PDF document (document to copy TO)
 * @param page_ranges Array of page ranges to copy. Each range is a tuple [start, end] (1-based indices).
 * @param insert_at Position in destination document to insert the pages (0-based index).
 * @returns Promise that resolves when the pages are copied
 */
export const CopyPage = async (
  { pdfium, doc_pointer }: PDFDocument,
  to_doc: NewDocument,
  page_ranges: [number, number][],
  insert_at: number
): Promise<NewDocument> => {
  // Create page range string from the ranges

  let added_pages = 0

  const page_range = page_ranges
    .map(([min, max]) => {
      added_pages += max - min + 1

      if (min === max) {
        return `${min}`
      }

      return `${min}-${max}`
    })
    .join(",")

  // Convert JavaScript string to C string
  const page_range_byte_size = StringToUInt8(page_range).length + 1
  const pageRangePtr = pdfium.pdfium.wasmExports.malloc(page_range_byte_size)
  pdfium.pdfium.stringToUTF8(page_range, pageRangePtr, page_range_byte_size)

  // Import the page from source document to destination document
  const import_operation = pdfium.FPDF_ImportPages(
    to_doc.doc_pointer, // Destination document
    doc_pointer, // Source document
    pageRangePtr, // Page range string
    insert_at - 1 // Index where to insert in destination doc
  )

  // Check if the operation was successful
  if (!import_operation) {
    throw GetLastError(pdfium)
  }

  pdfium.pdfium.wasmExports.free(pageRangePtr)

  // Increment the page count in the destination document
  to_doc.page_count += added_pages

  return to_doc
}
